﻿import { defineComponent, PropType } from 'vue';

import { week_day } from "../../../../objects/front_end/calendar/week_day";
import { capacity_capacity } from "../../../../objects/v1_3/product/capacity/capacity_capacity";

import { Booking } from "../../../../services/booking";
import { Currency_Service } from "../../../../../system/services/currency";
import { Product } from '../../../../services/product';
import { Date_Service } from '../../../../../system/services/date_service';
import { Capacity } from '../../../../services/capacity';

const Prod = new Product();
const Capa = new Capacity();
const Date_Services = new Date_Service();
const Book = new Booking();
const Currency_Services = new Currency_Service();

export default defineComponent({
    name: "calendar_day",
    props: {
        day: Object as PropType<week_day>,
        selected_tour_id: String,
        trip_index: Number,
        addon_departure_dates: { type: Array as PropType<string[]>, default: () => [] },
        capacities: { type: Array as PropType<capacity_capacity[]>, default: () => [] }
    },
    data() {
        return {
            // No longer used: replaced by Is_Low(), which has no side effect during render.
            // almostFull: false as boolean
        }
    },
    emits: [
        "dateChange"
    ],
    // the search page's travel date (1_search.ts provide), null elsewhere
    inject: {
        preferredDeparture: { default: null }
    },
    methods: {
        // a date clicked by the user (not the automatic preselect below): also make it the search's travel date
        async date_clicked() {
            await this.date_selected();

            const trip = this.$store.getters.get_Basket?.trips?.[this.get_Trip_Index()];
            if (trip?.departure_date && trip.departure_date == this.day?.date && this.day?.output_date) {
                this.preferredDeparture?.set?.(this.day.output_date);
            }
        },
        hasAddOn(): boolean {
            const addonIds = import.meta.env.VITE_CALENDAR_ADDON_IDS?.split(",").map((id: string) => id.trim().toUpperCase()) ?? [];
            if (!addonIds.length || !this.day?.date || !this.selected_tour_id) return false;

            const product_detail = Prod.Get_Details_Sync(this.selected_tour_id, this.$store);
            if (!product_detail?.seller_information?.add_ons) return false;

            let can_add = false;

            product_detail.seller_information.add_ons.forEach(addonGroup => {
                if (addonGroup.name == 'Add Ons') {
                    addonGroup.groups.forEach(group => {
                        if (addonIds.includes(group.products[0]?.tour_id?.toUpperCase())) {
                            if (!group.dates || group.dates.length == 0) {
                                can_add = true;
                            } else {
                                group.dates.forEach(daterange => {
                                    if (Date_Services.compare_dates_between(this.day?.date ?? null, daterange.date_from, daterange.date_to)) {
                                        can_add = true;
                                    }
                                });
                            }
                        }
                    });
                }
            });

            if (!can_add) return false;

            const searchDate = this.day.date.replaceAll("/", "-") + " ";
            return this.addon_departure_dates.some(d =>
                d.replaceAll("-0", "-").startsWith(searchDate)
            );
        },
        // No longer used: Not_Enough_Seats() counts the basket's passengers instead (the quote's are missing while it's calculated).
        // get_passangers_count(): number {
        //     let index = 0;
        //     this.get_Booking_Trip()?.passengers.forEach(pax => {
        //         index++;
        //     });
        //     return index;
        // },
        getDay(): string {
            if (this.day == undefined) {
                return "";
            }

            if (this.day.totalCapacity == -1) {
                return "";
            }

            return String(this.day.number);
        },
        // How a departure date is shown. Only scarce dates get a label, so they stand out:
        //   plenty: more than 5 seats, no label | few: 4-5 seats | last: 1-3 seats, "Nearly gone" (no exact numbers)
        //   short: fewer seats than the passengers selected, can't be booked | full: sold out
        Availability(): "none" | "plenty" | "few" | "last" | "short" | "full" {
            if (this.day == undefined || this.day.totalCapacity == -1) {
                return "none";
            }
            if (this.isFull()) {
                return "full";
            }
            if (this.Not_Enough_Seats()) {
                return "short";
            }

            const seats = this.get_Total_Capacity();
            if (seats <= 3) {
                return "last";
            }
            if (seats <= 5) {
                return "few";
            }
            return "plenty";
        },
        getSeats(): string {
            switch (this.Availability()) {
                case "full": return "Sold out";
                case "short": return "Not enough seats";
                case "last": return "Nearly gone";
                case "few": return "Few left";
                default: return "";
            }
        },
        isToday(): boolean {
            if(this.day?.is_today == true)
            {
                return true;
            }
            else
            {
                return false;
            }
        },
        getPrice(): string {
            if (this.day == undefined) {
                return "";
            }

            if (this.day.totalCapacity == -1) {
                return "";
            }

            if (this.day.fromPrice == -1) {
                return "";
            }

            return Currency_Services.Format_Currency(this.day.fromPrice, this.$store.getters.get_Currency);
        },
        display_day(): boolean {
            let retrurnval = true;

            if(this.getPrice() == "")
            {
                retrurnval = false;
            }
            if (this.day && this.day.fromPrice < 1)
            {
                retrurnval = false;
            }
            // was getSeats() == '', which only happened without a departure; plentiful dates now have no seats label
            if (this.Availability() == "none")
            {
                retrurnval = false;
            }

            return retrurnval
        },
        async date_selected() {
            if (this.day == undefined) {
                return;
            }

            if (this.day.totalCapacity == -1) {
                return;
            }

            if (this.isFull() == true) {
                return;
            }

            if (this.Not_Enough_Seats())
            {
                return;
            }

            this.$store.commit("Set_seats_free_selected_date", { Seats: this.get_Total_Capacity() });

            this.$emit("dateChange", this.day);

            this.$store.commit("Initialise_Basket_Departure", { Trip_Index: this.get_Trip_Index(), Departure_Date: this.day.date, Capacity: this.get_Booking_Capacity() });
            await Book.Setup_Booking(this.$store);
        },
        get_Booking_Capacity(): capacity_capacity | undefined {
            //a not for sale calendar tour is booked on the selected sales group option, so use that option's capacity for this date
            let Booking_Tour_Id = this.$store.getters.get_Basket?.trips[this.get_Trip_Index()]?.tour_id;
            if (!Prod.Is_Not_For_Sale(this.selected_tour_id ?? "", this.$store) || !Booking_Tour_Id || Booking_Tour_Id == this.selected_tour_id) {
                return this.day?.capacity_info;
            }

            return this.capacities.find(capa => capa.tour_id == Booking_Tour_Id && Date_Services.compare_date(capa.start_date_time) == Date_Services.compare_date(this.day?.output_date ?? null))
                ?? this.day?.capacity_info;
        },
        get_Total_Capacity(): number {
            //a not for sale calendar tour shows the seats left on the sales group option that is booked
            let Booking_Capacity = this.get_Booking_Capacity();
            if (this.day == undefined || this.day.totalCapacity == -1 || Booking_Capacity == undefined || Booking_Capacity == this.day.capacity_info) {
                return this.day?.totalCapacity ?? 0;
            }

            return Capa.Get_Free_Seats(Booking_Capacity);
        },
        isDate_Selected(): boolean {
            let Basket = this.$store.getters.get_Basket;

            let queryDate = this.GetDepartureDateFromQuery();
            let calendardate = this.day?.date?.replaceAll("/","-");
            let isQueryDateCalendarDate : boolean = (queryDate == calendardate);
            let isDateSelectedInBasket : boolean = false;
            if (this.get_Trip_Index() > -1 && Basket.trips[this.get_Trip_Index()] != undefined) {
                isDateSelectedInBasket= (Basket.trips[this.get_Trip_Index()].departure_date != null && Basket.trips[this.get_Trip_Index()].departure_date != "");
            }

            if (Basket.trips.length > this.get_Trip_Index())
            {
                if (Basket.trips[this.get_Trip_Index()].departure_date == this.day?.date) {
                    return true;
                }
                else if (isQueryDateCalendarDate && !isDateSelectedInBasket)
                {
                    this.date_selected();
                    return true;
                }
                // no date picked yet for this tour: preselect the search's travel date when the tour departs then and
                // it can be booked (same as ?departuredate= above, but skipping full/unavailable days)
                else if (!isDateSelectedInBasket && this.isPreferredDeparture() && (this.day?.totalCapacity ?? -1) != -1 && !this.isFull())
                {
                    this.date_selected();
                    return true;
                }
            }

            return false;
        },
        isPreferredDeparture(): boolean {
            const preferred: string = this.preferredDeparture?.get?.() ?? "";
            return preferred != "" && preferred == (this.day?.output_date ?? "");
        },
        isFull(): boolean {
            if ((this.day?.totalCapacity ?? 0) == -1) {
                return false;
            }

            if (this.day?.tour_id.findIndex((f: string) => f == this.selected_tour_id) == -1) {
                return true;
            }
            // No longer used: see Is_Low().
            // if (this.get_Total_Capacity() <= 4) {
            //     this.almostFull = true;
            // }

            return (this.get_Total_Capacity() == 0);
        },
        // Few seats left: the dates that get a scarcity label (see Availability)
        Is_Low(): boolean {
            const availability = this.Availability();
            return availability == "few" || availability == "last";
        },
        // Fewer seats than the passengers selected. Counts the basket's passengers (the quote's are missing until it's
        // calculated and while it's recalculated); the date can't be selected then.
        Not_Enough_Seats(): boolean {
            const passengers = this.$store.getters.get_Basket?.passengers?.length ?? 0;
            return !this.isFull() && passengers > this.get_Total_Capacity();
        },
        Day_Label(): string {
            // a full date shows no price, so it isn't read out either
            const parts = [this.day?.date ?? this.getDay(), this.isFull() ? "" : this.getPrice(), this.getSeats()].filter(p => p !== "");
            return parts.join(", ");
        },
        Day_Title(): string {
            if (this.isFull()) {
                return "This departure is sold out";
            }
            if (this.Not_Enough_Seats()) {
                return "Not enough seats left for your group on this date";
            }
            return "";
        },
        get_Trip_Index(): number {
            return this.trip_index ?? 0;
        },
        // the quote's trip for this basket trip: the quote also lists the add-ons as trips, so it's matched on the tour
        // rather than on get_Trip_Index() (which indexes get_Basket.trips)
        get_Booking_Trip(): any {
            const tour_id = this.$store.getters.get_Basket?.trips?.[this.get_Trip_Index()]?.tour_id;
            return (this.$store.getters.get_Booking?.trips ?? []).find((Trip: any) => Trip.tour_id == tour_id);
        },
        GetDepartureDateFromQuery() : string {
            let url = window.location.search.substring(1).replace("%20"," ");
            let params = url.split("&");
            let depDate = "";

            if(params.length > 0)
            {
                params.forEach((param: string) => {
                    if(param.includes("departuredate"))
                    {
                        depDate = param.split("=")[1];
                    }  
                });
            }

            return depDate;
        }
    },
    mounted() {
    }
})